import React from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import BlogCard from "./BlogCard.js";
import BlogData from "../../JsonFiles/Blogs.json";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./Blog.css";

const CarouselArrow = ({ direction, className, onClick }) => {
  const isPrevious = direction === "previous";

  return (
    <button
      type="button"
      className={`${className || ""} blog-slider__arrow blog-slider__arrow--${direction}`}
      onClick={onClick}
      aria-label={isPrevious ? "Show previous article" : "Show next article"}
    >
      <FontAwesomeIcon
        icon={isPrevious ? faArrowLeft : faArrowRight}
        aria-hidden="true"
      />
    </button>
  );
};

const Blog = ({ id = "insights" }) => {
  const articles = Array.isArray(BlogData) ? BlogData : [];
  const hasMultipleArticles = articles.length > 1;

  const settings = {
    dots: hasMultipleArticles,
    arrows: hasMultipleArticles,
    infinite: hasMultipleArticles,
    speed: 650,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: hasMultipleArticles,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    pauseOnFocus: true,
    pauseOnDotsHover: true,
    swipeToSlide: true,
    adaptiveHeight: true,
    accessibility: true,
    cssEase: "cubic-bezier(0.22, 1, 0.36, 1)",
    prevArrow: <CarouselArrow direction="previous" />,
    nextArrow: <CarouselArrow direction="next" />,
    customPaging: (index) => (
      <button type="button" aria-label={`Go to article ${index + 1}`}>
        <span />
      </button>
    ),
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <section id={id} className="blog-section" aria-labelledby="blog-title">
      <div className="blog-section__container">
        <header className="blog-section__header">
          <div className="blog-section__heading">
            <p className="blog-section__eyebrow">
              <span aria-hidden="true" />
              Ideas &amp; insights
            </p>
            <h2 id="blog-title">Thinking that moves business forward.</h2>
          </div>

          <div className="blog-section__intro">
            <p>
              Practical perspectives on product engineering, digital
              transformation, technology strategy, and the decisions behind
              better software.
            </p>
            <Link
              to="/blog"
              className="blog-section__all-link blog-section__all-link--desktop"
              onClick={scrollToTop}
            >
              View all insights
              <span aria-hidden="true">
                <FontAwesomeIcon icon={faArrowRight} />
              </span>
            </Link>
          </div>
        </header>

        {articles.length > 0 ? (
          <div
            className="blog-section__carousel"
            aria-label="Featured Axiomos articles"
          >
            <Slider {...settings}>
              {articles.map((article, index) => (
                <div
                  key={article.id ?? article.slug ?? index}
                  className="blog-section__slide"
                >
                  <div className="blog-section__slide-inner">
                    <span
                      className="blog-section__article-number"
                      aria-hidden="true"
                    >
                      Insight / {String(index + 1).padStart(2, "0")}
                    </span>
                    <BlogCard Blog={article} />
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        ) : (
          <div className="blog-section__empty" role="status">
            <p>New insights are being prepared. Please check back soon.</p>
          </div>
        )}

        <footer className="blog-section__footer">
          <p>
            <span>{String(articles.length).padStart(2, "0")}</span>
            {articles.length === 1 ? "featured article" : "featured articles"}
          </p>
          <Link
            to="/blog"
            className="blog-section__all-link blog-section__all-link--mobile"
            onClick={scrollToTop}
          >
            View all insights
            <span aria-hidden="true">
              <FontAwesomeIcon icon={faArrowRight} />
            </span>
          </Link>
        </footer>
      </div>
    </section>
  );
};

export default Blog;
